// Session bootstrap against our Pages Functions:
//   GET /api/auth/me -> 200 user | 401 (try one refresh, then sign in) | 403 no access
import { api, ApiError } from '../api/client';
import { isRole } from '../config/roles';
import type { SessionUser } from './types';

export const AUTH_PORTAL = import.meta.env.VITE_AUTH_PORTAL || 'https://auth.harney-teas.com';

export type SessionResult =
  | { kind: 'ok'; user: SessionUser }
  | { kind: 'unauthenticated' }
  | { kind: 'no_access'; message: string }
  | { kind: 'error'; message: string };

interface MeResponse {
  user: SessionUser;
}

async function me(): Promise<SessionUser> {
  const res = await api.get<MeResponse>('/api/auth/me');
  if (!res?.user || !isRole(res.user.role)) throw new ApiError(403, 'unknown_role', 'Unknown role');
  return res.user;
}

export async function loadSession(): Promise<SessionResult> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      return { kind: 'ok', user: await me() };
    } catch (err) {
      if (!(err instanceof ApiError)) return { kind: 'error', message: String(err) };
      if (err.status === 403) {
        return { kind: 'no_access', message: 'Your Harney account does not have access to Harney Dashboards.' };
      }
      if (err.status !== 401) return { kind: 'error', message: err.message };
      if (attempt > 0) break;
      try {
        await api.post('/api/auth/refresh');
      } catch {
        break;
      }
    }
  }
  return { kind: 'unauthenticated' };
}

export function signInUrl(returnTo = window.location.href): string {
  return `${AUTH_PORTAL}/login?redirect=${encodeURIComponent(returnTo)}`;
}

export async function logoutRequest(): Promise<void> {
  try {
    await api.post('/api/auth/logout');
  } catch {
    // Cookies may already be gone; the redirect below still ends the session view.
  }
}

/** Dev server only: VITE_DEV_AUTH_ROLE skips Harney Identity. Compiled out of production builds. */
export function devUser(): SessionUser | null {
  if (!import.meta.env.DEV) return null;
  const role = import.meta.env.VITE_DEV_AUTH_ROLE;
  if (!isRole(role)) return null;
  return {
    id: 'dev',
    email: 'dev@localhost',
    name: 'Local Developer',
    companySlug: 'harney',
    role,
    expiresAt: Math.floor(Date.now() / 1000) + 86400,
  };
}
